/**
 * Favourites — optimistic REST sync for logged-in users; guests open signup modal.
 */

import { showToast } from './toast.js';

const STORAGE_KEY = 'dc_favourites';
const HEART_PATH =
	'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

/** @type {number[]} */
let favouriteIds = [];

function getConfig() {
	return window.doneCommerce || {};
}

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readLocal() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
	} catch {
		return [];
	}
}

function clearLocal() {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Ignore storage failures.
	}
}

function normalizeIds(payload) {
	if (!payload) {
		return [];
	}
	const raw = Array.isArray(payload.ids)
		? payload.ids
		: Array.isArray(payload.items)
			? payload.items
			: Array.isArray(payload)
				? payload
				: [];

	return [
		...new Set(
			raw
				.map((item) =>
					typeof item === 'object' ? Number(item.product_id || item.id) : Number(item)
				)
				.filter(Boolean)
		),
	];
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		...options,
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	if (!response.ok) {
		const error = new Error('Request failed');
		error.status = response.status;
		try {
			error.data = await response.json();
		} catch {
			// ignore
		}
		throw error;
	}

	return response.json();
}

function updateHeaderCount(ids = favouriteIds) {
	const count = ids.length;
	document.querySelectorAll('[data-favourites-count]').forEach((el) => {
		el.textContent = count > 0 ? String(count) : '';
		el.hidden = count === 0;
		el.setAttribute('data-count', String(count));
	});
}

function updateButtonLabels(btn, active) {
	const addLabel = getConfig().i18n?.addToFavourites || 'Add to favourites';
	const removeLabel = getConfig().i18n?.removeFromFavourites || 'Remove from favourites';
	btn.setAttribute('aria-pressed', active ? 'true' : 'false');
	btn.setAttribute('aria-label', active ? removeLabel : addLabel);
	// PDP favourite is icon-only — never replace the SVG with label text.
}

export function syncFavouriteButtons(ids = favouriteIds) {
	favouriteIds = normalizeIds({ ids });
	document.querySelectorAll('[data-favourite-toggle]').forEach((btn) => {
		const id = Number(btn.dataset.productId);
		updateButtonLabels(btn, favouriteIds.includes(id));
	});
	updateHeaderCount(favouriteIds);
}

export function getFavouriteIds() {
	return [...favouriteIds];
}

export function applyFavouriteIds(ids) {
	syncFavouriteButtons(normalizeIds({ ids }));
}

function pulseButton(btn) {
	if (!btn) {
		return;
	}
	btn.classList.remove('is-favourite-pulse');
	void btn.offsetWidth;
	btn.classList.add('is-favourite-pulse');
	window.setTimeout(() => btn.classList.remove('is-favourite-pulse'), 400);
}

function spawnFloatingHeart(btn) {
	if (!btn || prefersReducedMotion()) {
		return;
	}

	const rect = btn.getBoundingClientRect();
	const particle = document.createElement('span');
	particle.className = 'dc-favourite-float';
	particle.setAttribute('aria-hidden', 'true');
	particle.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24"><path d="${HEART_PATH}" fill="currentColor"/></svg>`;

	const size = 22;
	particle.style.left = `${rect.left + rect.width / 2 - size / 2}px`;
	particle.style.top = `${rect.top + rect.height / 2 - size / 2}px`;

	document.body.appendChild(particle);
	particle.addEventListener(
		'animationend',
		() => {
			particle.remove();
		},
		{ once: true }
	);
	window.setTimeout(() => particle.remove(), 900);
}

function celebrateFavouriteAdd(btn) {
	pulseButton(btn);
	spawnFloatingHeart(btn);
	showToast(getConfig().i18n?.favouriteAdded || 'Added to favourites');
}

function notifyFavouriteRemove() {
	showToast(getConfig().i18n?.favouriteRemoved || 'Removed from favourites');
}

function pushWishlist(productId, button) {
	const config = getConfig();
	document.dispatchEvent(
		new CustomEvent('dc:add_to_wishlist', {
			detail: {
				item_id: String(productId),
				item_name: button?.dataset.productName || '',
				value: Number(button?.dataset.productPrice || 0),
				currency: config.currency || '',
			},
		})
	);
}

async function toggleLoggedIn(productId, btn) {
	const previous = [...favouriteIds];
	const wasActive = previous.includes(productId);
	const next = wasActive
		? previous.filter((id) => id !== productId)
		: [...previous, productId];

	syncFavouriteButtons(next);

	if (wasActive) {
		pulseButton(btn);
		notifyFavouriteRemove();
	} else {
		celebrateFavouriteAdd(btn);
		pushWishlist(productId, btn);
	}

	try {
		const data = wasActive
			? await apiFetch(`done-commerce/v1/favourites?product_id=${productId}`, {
					method: 'DELETE',
				})
			: await apiFetch('done-commerce/v1/favourites', {
					method: 'POST',
					body: JSON.stringify({ product_id: productId }),
				});

		syncFavouriteButtons(normalizeIds(data));
	} catch {
		syncFavouriteButtons(previous);
	}
}

async function mergeLegacyLocal() {
	const config = getConfig();
	if (!config.isLoggedIn) {
		return;
	}

	const local = readLocal();
	if (!local.length) {
		return;
	}

	try {
		const data = await apiFetch('done-commerce/v1/favourites/merge', {
			method: 'POST',
			body: JSON.stringify({ ids: local }),
		});
		clearLocal();
		syncFavouriteButtons(normalizeIds(data));
	} catch {
		// Retry on next load.
	}
}

async function hydrateFromServer() {
	const config = getConfig();
	if (!config.isLoggedIn) {
		favouriteIds = [];
		syncFavouriteButtons([]);
		return;
	}

	try {
		const data = await apiFetch('done-commerce/v1/favourites');
		syncFavouriteButtons(normalizeIds(data));
	} catch {
		syncFavouriteButtons([]);
	}
}

/**
 * After quick-register, apply server favourites and auth flags.
 *
 * @param {{ ids?: number[], count?: number }} favourites
 */
export function onFavouritesAuthSuccess(favourites) {
	clearLocal();
	syncFavouriteButtons(normalizeIds(favourites));
}

export async function addFavouriteAfterAuth(productId) {
	if (!productId) {
		return;
	}

	const config = getConfig();
	if (!config.isLoggedIn) {
		return;
	}

	const next = favouriteIds.includes(productId)
		? favouriteIds
		: [...favouriteIds, productId];
	syncFavouriteButtons(next);

	const btn = document.querySelector(
		`[data-favourite-toggle][data-product-id="${productId}"]`
	);
	celebrateFavouriteAdd(btn);
	pushWishlist(productId, btn);

	try {
		const data = await apiFetch('done-commerce/v1/favourites', {
			method: 'POST',
			body: JSON.stringify({ product_id: productId }),
		});
		syncFavouriteButtons(normalizeIds(data));
	} catch {
		await hydrateFromServer();
	}
}

/**
 * Celebrate a favourite that was already persisted during quick-register.
 *
 * @param {number} productId
 */
export function celebrateFavouriteForProduct(productId) {
	const btn = document.querySelector(
		`[data-favourite-toggle][data-product-id="${productId}"]`
	);
	celebrateFavouriteAdd(btn);
}

function openGuestSignup(productId = 0) {
	document.dispatchEvent(
		new CustomEvent('dc:open-auth-modal', {
			detail: { productId: Number(productId) || 0 },
		})
	);
}

export function initFavourites() {
	document.addEventListener('click', (e) => {
		const headerBtn = e.target.closest('[data-favourites-toggle]');
		if (headerBtn) {
			e.preventDefault();
			const config = getConfig();
			if (config.isLoggedIn) {
				const url = config.favouritesUrl || config.accountUrl;
				if (url) {
					window.location.href = url;
				}
				return;
			}
			openGuestSignup(0);
			return;
		}

		const btn = e.target.closest('[data-favourite-toggle]');
		if (!btn) {
			return;
		}

		e.preventDefault();
		const productId = Number(btn.dataset.productId);
		if (!productId) {
			return;
		}

		const config = getConfig();
		if (!config.isLoggedIn) {
			openGuestSignup(productId);
			return;
		}

		toggleLoggedIn(productId, btn);
	});

	hydrateFromServer().then(() => mergeLegacyLocal());
}

/** @deprecated Use getFavouriteIds */
export { getFavouriteIds as getFavourites };
